import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { OtpPanel } from '@/customer/components/auth/otp-panel'
import {
  VERIFICATION_SUCCESS_HOLD_MS,
  VerificationSuccessPanel,
} from '@/customer/components/auth/verification-success-panel'
import { PageContainer } from '@/customer/components/page-container'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { meQueryKey, useGetMe } from '@/customer/hooks/use-get-me'
import { authClient } from '@/customer/lib/auth'
import { routes } from '@/customer/routes'

/**
 * Verify an existing member's email without changing it. Used from
 * banners and the profile screen when the member landed with an
 * unverified email (e.g. staff-created account, imported member),
 * distinct from `/email/update` which is for changing the value.
 *
 * Auto-dispatches an OTP on mount via better-auth's email-verification
 * flow, then verifies against `emailOtp.verifyEmail` which marks the
 * member's email verified without touching the session.
 */
export function EmailVerificationPage() {
  const homeUrl = routes.account.index
  const updateEmailUrl = routes.account.email.update
  useDocumentTitle('Verify your email')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useGetMe()
  const [isSuccess, setIsSuccess] = useState(false)

  if (!me.data) return <PageContainer backLink={{ to: homeUrl, label: 'Back to account' }} />

  const { email, emailVerified } = me.data.member
  if (!email) return <Navigate to={homeUrl} replace />
  if (emailVerified && !isSuccess) return <Navigate to={homeUrl} replace />

  function handleVerified() {
    setIsSuccess(true)
    queryClient.invalidateQueries({ queryKey: meQueryKey })
    window.setTimeout(() => navigate(homeUrl), VERIFICATION_SUCCESS_HOLD_MS)
  }

  return (
    <PageContainer
      backLink={{ to: homeUrl, label: 'Back to account' }}
      headline={[
        { text: 'Verify your', color: 'yellow' },
        { text: 'email', color: 'blue' },
      ]}
    >
      <AnimatePresence mode="wait">
        {isSuccess ? (
          <motion.div key="success">
            <VerificationSuccessPanel active={isSuccess} />
          </motion.div>
        ) : (
          <motion.div
            key="otp"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex flex-col gap-4"
          >
            <OtpPanel
              autoSend
              onSuccess={handleVerified}
              sendCode={async () => {
                const { data, error } = await authClient.emailOtp.sendVerificationOtp({
                  email,
                  type: 'email-verification',
                })
                if (error) throw error
                if (!data) throw new Error('Missing data')
              }}
              verifyCode={async (code) => {
                const { error } = await authClient.emailOtp.verifyEmail({ email, otp: code })
                if (error) throw error
              }}
              blurb={
                <p className="text-body-md text-foreground">
                  We&apos;ve sent a 6-digit code to <span className="font-semibold">{email}</span>.
                  Enter it below to confirm your email address.
                </p>
              }
              extras={
                <div className="flex items-center gap-2 text-label-sm text-muted-foreground">
                  <span>Not your email?</span>
                  <Link
                    to={updateEmailUrl}
                    className="font-semibold text-foreground underline underline-offset-4"
                  >
                    Update it
                  </Link>
                </div>
              }
            />
          </motion.div>
        )}
      </AnimatePresence>
    </PageContainer>
  )
}
